import { z } from 'zod';
export const CYCLE_CONSENT_PURPOSE = 'cycle';
export const cycleEventTypeSchema = z.enum(['period_start', 'period_end']);
export const cycleSymptomsSchema = z
    .object({
    bloating: z.boolean().optional(),
    cravings: z.boolean().optional(),
    energy: z.boolean().optional(),
    mood: z.boolean().optional(),
})
    .strict();
export const createCycleLogSchema = z.object({
    eventType: cycleEventTypeSchema,
    occurredOn: z.string().date(),
    symptoms: cycleSymptomsSchema.nullable().optional(),
});
export const cycleLogViewSchema = z.object({
    id: z.string().uuid(),
    eventType: cycleEventTypeSchema,
    occurredOn: z.string().date(),
    symptoms: cycleSymptomsSchema.nullable(),
    createdAt: z.string().datetime(),
});
export const cycleLogsResponseSchema = z.object({
    logs: z.array(cycleLogViewSchema),
});
export const cyclePhaseSchema = z.enum(['unknown', 'menstruation', 'follicular', 'luteal']);
export const cyclePredictionQualitySchema = z.enum(['none', 'low', 'usable']);
export const cyclePhaseQuerySchema = z.object({
    asOf: z.string().date().optional(),
    userSetLengthDays: z.coerce.number().int().min(21).max(45).optional(),
});
export const cyclePhaseResponseSchema = z.object({
    phase: cyclePhaseSchema,
    lateLutealWindow: z.boolean(),
    asOf: z.string().date(),
    nextPeriodStart: z.string().date().nullable(),
    cycleLengthDays: z.number().int().min(21).max(45).nullable(),
    predictionQuality: cyclePredictionQualitySchema,
    basedOnHistoryCycles: z.number().int().nonnegative(),
});
// ── Cycle insights (T19.5 / ADR-0030 §3) ──────────────────────────────────────
export const cycleNudgeKindSchema = z.enum(['hydration', 'nutrition']);
export const cycleNudgeSchema = z.object({
    key: z.string(),
    kind: cycleNudgeKindSchema,
});
export const cyclePhasePatternSchema = z.object({
    key: z.string(),
    phase: cyclePhaseSchema,
    params: z.record(z.string(), z.number()),
});
export const cycleWaterRetentionAlertSchema = z.object({
    /** True when today is in the late-luteal window and a rise is expected. */
    active: z.boolean(),
    /** Predicted next period start date (null when not usable). */
    nextPeriodStart: z.string().date().nullable(),
});
export const cycleInsightsResponseSchema = z.object({
    /** Pre-empt the scale bump: shown in the weekly summary when in late-luteal window. */
    waterRetentionAlert: cycleWaterRetentionAlertSchema,
    /** Phase-tagged macro patterns derived from the user's own logs. Empty until ≥2 cycles. */
    phasePatterns: z.array(cyclePhasePatternSchema),
    /** Additive nudges for the current phase (hydration / iron / fibre). Never restriction. */
    nudges: z.array(cycleNudgeSchema),
});
// ── Energy-availability signal (T19.6 / ADR-0030 §5 — high-care, ED-gated) ──────
/**
 * Gentle, non-clinical amenorrhea / low-energy-availability observation.
 * Disabled by default (double lock; ADR-0030 §5b). `active` is true only when
 * the signal should be surfaced once; the copy lives in i18n (cycle:signal.*)
 * and is NEVER a diagnosis.
 */
export const energyAvailabilitySignalResponseSchema = z.object({
    /** True only when eligible, gates open, and not yet surfaced (surface once). */
    active: z.boolean(),
    /** Whether the user has opted out of this signal. */
    optedOut: z.boolean(),
    /** ED-resource link (existing footer pattern); null unless the signal is active. */
    resourceUrl: z.string().url().nullable(),
});
//# sourceMappingURL=cycle.js.map